import { useSyncExternalStore } from 'react'

/**
 * What the daily weigh-in does on *this browser*, per the settings' Weigh-in section: whether
 * a notification asks for the day's weigh-in, and when.
 *
 * The reminder is the device's own and stays here. A notification is something one browser on
 * one machine can post, at a time that suits whoever is sitting at it — carrying it to another
 * device would be promising an alarm somewhere nobody is. What a day logs and whether the app
 * carries the nudge are facts about the person, and live on the account: see `AccountSettings`.
 */
export interface WeighInReminder {
  readonly enabled: boolean
  readonly time: string
}

const KEY = 'gymcloud.weighin-reminder'

const DEFAULT: WeighInReminder = { enabled: false, time: '08:00' }

function read(): WeighInReminder {
  try {
    const held = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<WeighInReminder>
    return {
      enabled: held.enabled === true,
      time: /^\d{2}:\d{2}$/.test(held.time ?? '') ? (held.time ?? DEFAULT.time) : DEFAULT.time,
    }
  } catch {
    return DEFAULT
  }
}

let held: WeighInReminder = read()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The reminder as it stands, kept live across every component reading it. */
export function useWeighInReminder(): WeighInReminder {
  return useSyncExternalStore(subscribe, () => held)
}

export function saveWeighInReminder(next: WeighInReminder): void {
  held = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // A full or blocked store loses persistence, not the session's own choice.
  }
  for (const listener of listeners) {
    listener()
  }
}
